import { McpAuthMode } from '@kanbu/schema/enums';
import { useLingui, Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  TextArea,
  toast,
} from '@utima/ui';
import { useEffect, useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { McpOAuthPanel } from './McpOAuthPanel';
import { useMcpIntegrationMutation } from '../hooks/useMcpIntegrationMutation';
import { useMcpOAuthConnect } from '../hooks/useMcpOAuthConnect';

type McpIntegrationRow = RouterOutputs['mcpIntegrations']['findAll'][number];

export interface McpIntegrationPrefill {
  name?: string;
  serverUrl?: string;
  authMode?: McpAuthMode;
  connectorId?: string;
}

export interface McpIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration?: McpIntegrationRow;
  prefill?: McpIntegrationPrefill;
}

interface FormState {
  name: string;
  description: string;
  serverUrl: string;
  authMode: McpAuthMode;
  apiKey: string;
  availableToAllChats: boolean;
  chatIds: string[];
  isActive: boolean;
  connectorId?: string;
  /** undefined = all discovered tools enabled; array = explicit allowlist. */
  enabledTools?: string[];
}

function initialState(
  integration?: McpIntegrationRow,
  prefill?: McpIntegrationPrefill,
): FormState {
  return {
    name: integration?.name ?? prefill?.name ?? '',
    description: integration?.description ?? '',
    serverUrl: integration?.serverUrl ?? prefill?.serverUrl ?? '',
    authMode:
      integration?.authMode ?? prefill?.authMode ?? McpAuthMode.StaticBearer,
    apiKey: '',
    availableToAllChats: integration?.availableToAllChats ?? false,
    chatIds: integration?.chatIds ?? [],
    isActive: integration?.isActive ?? true,
    connectorId: integration?.connectorId ?? prefill?.connectorId,
    enabledTools: integration?.enabledTools ?? undefined,
  };
}

export function McpIntegrationDialog({
  open,
  onOpenChange,
  integration,
  prefill,
}: McpIntegrationDialogProps) {
  const { t } = useLingui();
  const { create, update } = useMcpIntegrationMutation();
  const { connect } = useMcpOAuthConnect();
  const [form, setForm] = useState<FormState>(
    initialState(integration, prefill),
  );

  const isEdit = Boolean(integration);

  useEffect(() => {
    if (open) {
      setForm(initialState(integration, prefill));
    }
  }, [open, integration, prefill]);

  const { data: chats } = trpc.chats.findAll.useQuery({
    pagination: { pageSize: 1000 },
    fields: ['id', 'name'],
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleChat = (chatId: string) =>
    setForm(prev => ({
      ...prev,
      chatIds: prev.chatIds.includes(chatId)
        ? prev.chatIds.filter(id => id !== chatId)
        : [...prev.chatIds, chatId],
    }));

  const discoveredTools = integration?.discoveredTools ?? [];

  const isToolEnabled = (name: string) =>
    !form.enabledTools || form.enabledTools.includes(name);

  const toggleTool = (name: string) =>
    setForm(prev => {
      const current =
        prev.enabledTools ?? discoveredTools.map(tool => tool.name);
      const next = current.includes(name)
        ? current.filter(toolName => toolName !== name)
        : [...current, name];

      return { ...prev, enabledTools: next };
    });

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.serverUrl.trim()) {
      toast.error(t`Name and server URL are required.`);

      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      serverUrl: form.serverUrl.trim(),
      authMode: form.authMode,
      availableToAllChats: form.availableToAllChats,
      chatIds: form.availableToAllChats ? [] : form.chatIds,
      isActive: form.isActive,
      ...(form.connectorId ? { connectorId: form.connectorId } : {}),
      ...(form.enabledTools ? { enabledTools: form.enabledTools } : {}),
      // Only send the key when entered — blank keeps the stored value on edit.
      ...(form.authMode === McpAuthMode.StaticBearer && form.apiKey
        ? { apiKey: form.apiKey }
        : {}),
    };

    try {
      const saved = await (integration
        ? update.mutateAsync({ id: integration.id, ...payload })
        : create.mutateAsync(payload));

      toast.success(t`Integration saved.`);
      onOpenChange(false);

      // New OAuth integrations have nowhere to click "Connect" yet — start the
      // provider consent flow immediately instead of making the admin reopen
      // the row.
      if (!integration && form.authMode === McpAuthMode.McpOAuth) {
        void connect(saved.id, `/agent-tools?tab=mcp&edit=${saved.id}`);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to save integration.`,
      );
    }
  };

  const chatItems = (chats?.items ?? []) as { id: string; name: string }[];
  const isPending = create.isPending || update.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[640px] max-w-[95vw]'>
        <Dialog.Header>
          <Dialog.Title>
            {isEdit ? (
              <Trans>Edit MCP Integration</Trans>
            ) : (
              <Trans>Add MCP Integration</Trans>
            )}
          </Dialog.Title>
        </Dialog.Header>

        <div className='max-h-[70vh] space-y-4 overflow-y-auto py-2'>
          <div className='space-y-1.5'>
            <Label htmlFor='mcp-name'>
              <Trans>Name</Trans>
            </Label>
            <Input
              id='mcp-name'
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder={t`e.g. Zendesk`}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='mcp-description'>
              <Trans>Description</Trans>
            </Label>
            <TextArea
              id='mcp-description'
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              rows={2}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='mcp-url'>
              <Trans>Server URL</Trans>
            </Label>
            <Input
              id='mcp-url'
              value={form.serverUrl}
              onChange={e => setField('serverUrl', e.target.value)}
              placeholder='https://mcp.example.com'
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='mcp-auth'>
              <Trans>Authentication</Trans>
            </Label>
            <Select
              id='mcp-auth'
              value={form.authMode}
              onValueChange={value =>
                setField('authMode', value as McpAuthMode)
              }
            >
              <SelectItem value={McpAuthMode.None}>
                {t`No authentication`}
              </SelectItem>
              <SelectItem value={McpAuthMode.StaticBearer}>
                {t`API key / bearer`}
              </SelectItem>
              <SelectItem value={McpAuthMode.McpOAuth}>
                {t`OAuth (MCP)`}
              </SelectItem>
            </Select>
          </div>

          {form.authMode === McpAuthMode.StaticBearer && (
            <div className='space-y-1.5'>
              <Label htmlFor='mcp-key'>
                <Trans>API key</Trans>
              </Label>
              <Input
                id='mcp-key'
                type='password'
                value={form.apiKey}
                onChange={e => setField('apiKey', e.target.value)}
                placeholder={
                  integration?.hasApiKey
                    ? t`Leave blank to keep the current key`
                    : t`Bearer token`
                }
              />
            </div>
          )}

          {form.authMode === McpAuthMode.McpOAuth &&
            (integration ? (
              <McpOAuthPanel
                integrationId={integration.id}
                initialStatus={integration.connectionStatus}
              />
            ) : (
              <p className='rounded-radius bg-muted/40 p-3 text-sm text-muted-fg'>
                <Trans>Saving will open the sign-in window to connect.</Trans>
              </p>
            ))}

          {discoveredTools.length > 0 && (
            <div className='space-y-1.5'>
              <Label>
                <Trans>Enabled tools</Trans>
              </Label>
              <div className='max-h-44 space-y-1 overflow-y-auto rounded-radius border border-border p-2'>
                {discoveredTools.map(tool => (
                  <label
                    key={tool.name}
                    className='flex cursor-pointer items-start gap-2 text-sm'
                    title={tool.description ?? undefined}
                  >
                    <input
                      type='checkbox'
                      className='mt-1'
                      checked={isToolEnabled(tool.name)}
                      onChange={() => toggleTool(tool.name)}
                    />
                    <span className='font-mono text-xs'>{tool.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className='flex items-center justify-between'>
            <Label htmlFor='mcp-all-chats'>
              <Trans>Available to all chats</Trans>
            </Label>
            <Switch
              id='mcp-all-chats'
              checked={form.availableToAllChats}
              onCheckedChange={checked =>
                setField('availableToAllChats', checked)
              }
            />
          </div>

          {!form.availableToAllChats && (
            <div className='space-y-1.5'>
              <Label>
                <Trans>Assigned chats</Trans>
              </Label>
              <div className='max-h-40 space-y-1 overflow-y-auto rounded-radius border border-border p-2'>
                {chatItems.length === 0 && (
                  <p className='text-sm text-muted-fg'>
                    <Trans>No chats available.</Trans>
                  </p>
                )}
                {chatItems.map(chat => (
                  <label
                    key={chat.id}
                    className='flex cursor-pointer items-center gap-2 text-sm'
                  >
                    <input
                      type='checkbox'
                      checked={form.chatIds.includes(chat.id)}
                      onChange={() => toggleChat(chat.id)}
                    />
                    {chat.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className='flex items-center justify-between'>
            <Label htmlFor='mcp-active'>
              <Trans>Active</Trans>
            </Label>
            <Switch
              id='mcp-active'
              checked={form.isActive}
              onCheckedChange={checked => setField('isActive', checked)}
            />
          </div>
        </div>

        <Dialog.Footer>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button loading={isPending} onClick={handleSubmit}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
